import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Loader from "react-loader-spinner";
import { FiPlus, FiEdit2, FiExternalLink } from "react-icons/fi";
import BottomModal from '../components/BottomModal';
import PartnerModal from '../components/PartnerModal';
import { fetchAdminLeagueStructure } from '../actions/leaguesActions';
import {
    fetchAdminPartners,
    savePartner,
    deletePartner,
    partnerErrorText,
} from '../actions/partnersActions';
import { BASE_URL } from '../constants/config';
import { buildLeagueStructure } from '../utilities/leagueStructure';
import { leagueGroupsFrom, plural } from '../utilities/partners';
import '../styles/leagueManagement.css';
import '../styles/partners.css';

// Управление спонсорами: общий список спонсоров клиента, их данные и лиги,
// где их показывать. Порядок спонсоров внутри лиги — на вкладке «Спонсоры»
// в настройках лиги. Руты — PARTNERS_BACKEND.md.

const resolveImage = (image) => {
    if (!image) return null;
    return image.startsWith('http') || image.startsWith('data:') ? image : BASE_URL.url + image;
};

const hostOf = (href) => href.replace(/^https?:\/\//i, '').replace(/\/$/, '');

export default function PartnersManagement() {
    const dispatch = useDispatch();

    const [partners, setPartners] = useState([]);
    const [structurePayload, setStructurePayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [modal, setModal] = useState({ open: false, partner: null });
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ text: '', type: 'success', visible: false, id: 0 });

    const showToast = (text, type = 'success') => {
        setToast({ text, type, visible: true, id: Date.now() });
    };

    useEffect(() => {
        if (!toast.visible) return undefined;
        const timer = setTimeout(() => {
            setToast(prev => (prev.id === toast.id ? { ...prev, visible: false } : prev));
        }, 5000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.visible]);

    const loadPartners = useCallback(async () => {
        const list = await dispatch(fetchAdminPartners());
        setPartners(list);
    }, [dispatch]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [list, payload] = await Promise.all([
                dispatch(fetchAdminPartners()),
                dispatch(fetchAdminLeagueStructure()),
            ]);
            setPartners(list);
            setStructurePayload(payload || { championats: [], orphan_leagues: [] });
        } catch (e) {
            console.error('Ошибка загрузки спонсоров:', e);
            setError(partnerErrorText(e, 'Не удалось загрузить спонсоров'));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const structure = useMemo(() => buildLeagueStructure(structurePayload), [structurePayload]);
    const groups = useMemo(() => leagueGroupsFrom(structure), [structure]);

    const visiblePartners = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return partners;
        return partners.filter(partner => [partner.name, partner.description, partner.href]
            .some(value => (value || '').toLowerCase().includes(q)));
    }, [partners, query]);

    const handleSave = async (result) => {
        setSaving(true);
        const isNew = !modal.partner;
        try {
            await dispatch(savePartner(modal.partner, result));
            await loadPartners();
            showToast(isNew ? 'Спонсор добавлен' : 'Спонсор сохранён');
            return true;
        } catch (e) {
            console.error('Ошибка сохранения спонсора:', e);
            showToast(partnerErrorText(e, 'Не удалось сохранить спонсора'), 'error');
            // Часть изменений могла пройти — показываем то, что на сервере
            loadPartners().catch(() => {});
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (partner) => {
        setSaving(true);
        try {
            await dispatch(deletePartner(partner.id));
            await loadPartners();
            showToast(`Спонсор «${partner.name}» удалён`);
            return true;
        } catch (e) {
            console.error('Ошибка удаления спонсора:', e);
            showToast(partnerErrorText(e, 'Не удалось удалить спонсора'), 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const leagueSummary = (partner) => {
        const count = partner.leagues.length;
        if (count === 0) return <span className="ptMuted">не показывается ни в одной лиге</span>;
        const names = partner.leagues
            .map(link => structure.leaguesById[link.league_id])
            .filter(Boolean)
            .map(league => league.name);
        const hidden = partner.leagues.filter(link => !link.active).length;
        const shown = names.slice(0, 2).join(', ');
        const rest = count - Math.min(2, names.length);
        return (
            <>
                <span className="ptLeagueCount">
                    {count} {plural(count, 'лига', 'лиги', 'лиг')}
                </span>
                {shown && <span className="ptMuted">{shown}{rest > 0 ? ` и ещё ${rest}` : ''}</span>}
                {hidden > 0 && <span className="ptTag">выключен в {hidden}</span>}
            </>
        );
    };

    const renderPartner = (partner) => {
        const image = resolveImage(partner.image);
        return (
            <div
                key={partner.id}
                className="ptRow"
                role="button"
                tabIndex={0}
                onClick={() => setModal({ open: true, partner })}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') setModal({ open: true, partner });
                }}
            >
                <div className="ptRowLogo">
                    {image ? <img src={image} alt="" loading="lazy" /> : partner.name.charAt(0).toUpperCase()}
                </div>
                <div className="ptRowMain">
                    <span className="ptRowTitle">{partner.name}</span>
                    {partner.description && <span className="ptMuted">{partner.description}</span>}
                    {partner.href && (
                        <a
                            className="ptRowLink"
                            href={partner.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {hostOf(partner.href)} <FiExternalLink />
                        </a>
                    )}
                </div>
                <div className="ptRowLeagues">{leagueSummary(partner)}</div>
                <span className="lgLeagueAction ptRowAction">
                    <FiEdit2 />
                    Изменить
                </span>
            </div>
        );
    };

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="matchCenterHeader">
                    <span className="matchCenterName">Управление спонсорами</span>
                </div>

                <div className="lgPage">
                    <div className="lgSectionHead">
                        <div>
                            <h2>Спонсоры</h2>
                            <p>
                                Логотипы спонсоров показываются на страницах лиг. Здесь —
                                данные спонсора и лиги, где его показывать. Порядок внутри
                                лиги — в настройках лиги, на вкладке «Спонсоры».
                            </p>
                        </div>
                        <button
                            className="lgPrimaryBtn"
                            disabled={saving || loading || Boolean(error)}
                            onClick={() => setModal({ open: true, partner: null })}
                        >
                            <FiPlus />
                            Добавить спонсора
                        </button>
                    </div>

                    {loading ? (
                        <div className="lgLoader">
                            <Loader type="Oval" color="#04a9f5" height={64} width={64} />
                            <p>Загрузка спонсоров…</p>
                        </div>
                    ) : error ? (
                        <div className="lgError">
                            {error}{' '}
                            <button type="button" className="linkButton" onClick={loadAll}>Повторить</button>
                        </div>
                    ) : partners.length === 0 ? (
                        <div className="lgEmptyState">
                            <p>Спонсоров пока нет.</p>
                            <button className="lgPrimaryBtn" onClick={() => setModal({ open: true, partner: null })}>
                                <FiPlus />
                                Добавить первого спонсора
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="ptToolbar">
                                <input
                                    type="text"
                                    className="modalInput ptSearch"
                                    placeholder="Найти спонсора"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                                <span className="ptMuted">
                                    {visiblePartners.length} из {partners.length}
                                </span>
                            </div>
                            <div className="ptList">
                                {visiblePartners.length > 0
                                    ? visiblePartners.map(renderPartner)
                                    : <p className="ptEmpty">Ничего не найдено.</p>}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <PartnerModal
                isOpen={modal.open}
                partner={modal.partner}
                groups={groups}
                initialLeagueIds={[]}
                saving={saving}
                onClose={() => setModal({ open: false, partner: null })}
                onSave={handleSave}
                onDelete={handleDelete}
            />

            <div className={`matchResultsModalWrapper${toast.visible ? ' matchResultsModalWrapperActive' : ''}`}>
                <BottomModal text={toast.text} type={toast.type} />
            </div>
        </div>
    );
}
